import { useState, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  VStack,
  Text,
} from '@chakra-ui/react';
import Cropper from 'react-easy-crop';
import { Point, Area } from 'react-easy-crop/types';

interface ImageCropModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  onCropComplete: (croppedImageBase64: string) => void;
}

export const ImageCropModal = ({ isOpen, onClose, imageSrc, onCropComplete }: ImageCropModalProps) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropChange = (location: Point) => {
    setCrop(location);
  };

  const onZoomChange = (zoom: number) => {
    setZoom(zoom);
  };

  const onCropCompleteInternal = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createCroppedImage = async () => {
    if (!croppedAreaPixels) return;

    const image = new Image();
    image.src = imageSrc;

    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;

    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height
    );

    canvas.toBlob((blob) => {
      if (!blob) return;
      
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result as string;
        onCropComplete(base64data);
        onClose();
      };
    }, 'image/jpeg', 0.95);
  };

  const handleSkipCrop = () => {
    // Use original image without cropping
    onCropComplete(imageSrc);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <VStack align="start" spacing={1}>
            <Text>Crop Image (Optional)</Text>
            <Text fontSize="sm" fontWeight="normal" color="gray.600">
              Drag to reposition, use slider to zoom, or skip crop to use original image
            </Text>
          </VStack>
        </ModalHeader>
        <ModalBody>
          <VStack spacing={4}>
            <Box position="relative" width="100%" height="400px">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={16 / 9}
                onCropChange={onCropChange}
                onZoomChange={onZoomChange}
                onCropComplete={onCropCompleteInternal}
              />
            </Box>
            <Box width="100%">
              <Text fontSize="sm" mb={2}>Zoom</Text>
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={onZoomChange}
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr="auto" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="outline" colorScheme="blue" mr={3} onClick={handleSkipCrop}>
            Use Original (No Crop)
          </Button>
          <Button colorScheme="blue" onClick={createCroppedImage}>
            Apply Crop
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
